import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Card, Field, Input, Loading } from "@/components/Ui";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setError("This invitation link is incomplete."); return; }
    api.get(`/invites/${token}`)
      .then((r) => { setInvite(r.data); setForm((f) => ({ ...f, name: r.data.name || "" })); })
      .catch((e) => setError(errMsg(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/invites/accept", { token, name: form.name, password: form.password });
      await refresh?.();
      toast.success("Welcome to the team");
      navigate("/supplier");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  if (error) {
    return (
      <section className="mx-auto max-w-[560px] px-5 py-20" data-testid="invite-error">
        <Card>
          <h1 className="font-display text-2xl font-black tracking-tight text-[#102A43]">
            Invitation unavailable
          </h1>
          <p className="mt-3 text-sm text-[#667788]">{error}</p>
        </Card>
      </section>
    );
  }
  if (!invite) return <Loading label="Checking your invitation…" />;

  return (
    <section className="mx-auto max-w-[560px] px-5 py-16" data-testid="accept-invite-page">
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#147D78]">
        Team invitation
      </p>
      <h1 className="mt-2 font-display text-3xl font-black tracking-tight text-[#102A43]">
        Join {invite.company_name}
      </h1>
      <p className="mt-3 text-sm text-[#667788]">
        {invite.invited_by} invited you as{" "}
        {invite.level === "admin" ? "a company administrator" : "a team member"}. Set a password to
        activate your access — you will sign in with {invite.email}.
      </p>
      <Card className="mt-6">
        <form onSubmit={submit} className="grid gap-3">
          <Field label="Full name">
            <Input data-testid="invite-accept-name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </Field>
          <Field label="Password" hint="At least 8 characters.">
            <Input data-testid="invite-accept-password" type="password" minLength={8}
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              required />
          </Field>
          <Field label="Confirm password">
            <Input data-testid="invite-accept-confirm" type="password" value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })} required />
          </Field>
          <Btn data-testid="invite-accept-submit" type="submit" loading={busy} className="mt-2 w-full">
            Activate my access
          </Btn>
        </form>
      </Card>
    </section>
  );
}
